import { z } from 'zod';
import { PartType } from '../../constants/part-type.js';
import { PRODUCT_CONSTRAINTS } from '../product/constraints.js';
import { optionalNonNegativeIntString, optionalNonNegativeNumberString, optionalUuidString, requiredEnumIdString, requiredUuidString, } from '../zod-helpers/index.js';
import { PART_CONSTRAINTS } from './constraints.js';
import { applyPartCreateWizardRefinements, refineLocation, refineProduct, refineVendor, } from './create-form-refinements.js';
// FOOTGUN: keep numeric fields as z.string() — RHF coerces empty number inputs to 0.
export const partCreateProductModeSchema = z.enum(['none', 'create']);
export const partCreateFormFieldsSchema = z.object({
    num: z
        .string()
        .trim()
        .min(1, `${PART_CONSTRAINTS.num.label} is required.`)
        .max(PART_CONSTRAINTS.num.maxLength),
    name: z.string().max(PART_CONSTRAINTS.name.maxLength).optional(),
    description: z
        .string()
        .max(PART_CONSTRAINTS.description.maxLength, `${PART_CONSTRAINTS.description.label} is too long.`),
    revision: z.string().trim().max(PART_CONSTRAINTS.revision.maxLength),
    typeId: requiredEnumIdString(PartType, 'Part type is required.', 'Part type must be a valid selection.'),
    trackingFlag: z.boolean(),
    serializedFlag: z.boolean(),
    controlledFlag: z.boolean(),
    uomId: requiredUuidString('Stocking UOM is required.', 'Invalid stocking UOM selection.'),
    partBrandIds: z
        .array(optionalUuidString('Invalid brand selection.'))
        .min(1, 'Select at least one brand.'),
    // Step 4 — product (conditional for sellable types)
    productMode: partCreateProductModeSchema,
    productNum: z.string().trim().max(PART_CONSTRAINTS.num.maxLength),
    productBrandId: optionalUuidString('Invalid brand selection.'),
    productName: z.string().max(PRODUCT_CONSTRAINTS.name.maxLength),
    productDescription: z.string().trim().max(PRODUCT_CONSTRAINTS.description.maxLength),
    productPrice: optionalNonNegativeNumberString('Product price'),
    productSku: z.string().max(PRODUCT_CONSTRAINTS.sku.maxLength),
    productUpc: z.string().max(PRODUCT_CONSTRAINTS.upc.maxLength),
    // Step 5 — location
    locationGroupId: optionalUuidString('Invalid warehouse selection.'),
    locationId: optionalUuidString('Invalid location selection.'),
    reorderPoint: optionalNonNegativeNumberString(PART_CONSTRAINTS.reorderPoint.label),
    orderUpToLevel: optionalNonNegativeNumberString(PART_CONSTRAINTS.orderUpToLevel.label),
    // Step 6 — vendor
    vendorId: optionalUuidString('Invalid vendor selection.'),
    vendorPartNumber: z.string().trim().max(PART_CONSTRAINTS.vendorPartNumber.maxLength),
    vendorLastCost: optionalNonNegativeNumberString(PART_CONSTRAINTS.vendorLastCost.label),
    vendorLeadTime: optionalNonNegativeIntString(PART_CONSTRAINTS.vendorLeadTime.label),
    vendorQtyMin: optionalNonNegativeNumberString(PART_CONSTRAINTS.vendorQtyMin.label),
    vendorPrimaryFlag: z.boolean(),
    // Step 7 — costing
    stdCost: optionalNonNegativeNumberString(PART_CONSTRAINTS.stdCost.label),
    leadTime: optionalNonNegativeIntString(PART_CONSTRAINTS.leadTime.label),
    inventoryAccountId: optionalUuidString('Invalid inventory account ID.'),
    cogsAccountId: optionalUuidString('Invalid COGS account ID.'),
    varianceAccountId: optionalUuidString('Invalid variance account ID.'),
    scrapAccountId: optionalUuidString('Invalid scrap account ID.'),
});
export const partCreateWizardStepFields = {
    1: ['num', 'name', 'description', 'revision', 'partBrandIds'],
    2: ['typeId', 'trackingFlag', 'serializedFlag', 'controlledFlag'],
    3: ['uomId'],
    4: [
        'productMode',
        'productNum',
        'productBrandId',
        'productName',
        'productDescription',
        'productPrice',
        'productSku',
        'productUpc',
    ],
    5: ['locationGroupId', 'locationId', 'reorderPoint', 'orderUpToLevel'],
    6: [
        'vendorId',
        'vendorPartNumber',
        'vendorLastCost',
        'vendorLeadTime',
        'vendorQtyMin',
        'vendorPrimaryFlag',
    ],
    7: [
        'stdCost',
        'leadTime',
        'inventoryAccountId',
        'cogsAccountId',
        'varianceAccountId',
        'scrapAccountId',
    ],
};
export const partCreateStep1Schema = partCreateFormFieldsSchema.pick({
    num: true,
    name: true,
    description: true,
    revision: true,
    partBrandIds: true,
});
export const partCreateStep2Schema = partCreateFormFieldsSchema.pick({
    typeId: true,
    trackingFlag: true,
    serializedFlag: true,
    controlledFlag: true,
});
export const partCreateStep3Schema = partCreateFormFieldsSchema.pick({ uomId: true });
export const partCreateStep4Schema = partCreateFormFieldsSchema
    .pick({
    productMode: true,
    productNum: true,
    productBrandId: true,
    productName: true,
    productDescription: true,
    productPrice: true,
    productSku: true,
    productUpc: true,
})
    .extend({ partBrandIds: z.array(z.string()) })
    .superRefine((values, context) => {
    if (values.productMode === 'none') {
        return;
    }
    refineProduct(values, context);
});
export const partCreateStep5Schema = partCreateFormFieldsSchema
    .pick({ locationGroupId: true, locationId: true, reorderPoint: true, orderUpToLevel: true })
    .superRefine(refineLocation);
export const partCreateStep6Schema = partCreateFormFieldsSchema
    .pick({
    vendorId: true,
    vendorPartNumber: true,
    vendorLastCost: true,
    vendorLeadTime: true,
    vendorQtyMin: true,
    vendorPrimaryFlag: true,
})
    .superRefine(refineVendor);
export const partCreateStep7Schema = partCreateFormFieldsSchema.pick({
    stdCost: true,
    leadTime: true,
    inventoryAccountId: true,
    cogsAccountId: true,
    varianceAccountId: true,
    scrapAccountId: true,
});
export const partCreateStepSchemas = {
    1: partCreateStep1Schema,
    2: partCreateStep2Schema,
    3: partCreateStep3Schema,
    4: partCreateStep4Schema,
    5: partCreateStep5Schema,
    6: partCreateStep6Schema,
    7: partCreateStep7Schema,
};
export const partCreateFormSchema = partCreateFormFieldsSchema.superRefine(applyPartCreateWizardRefinements);
//# sourceMappingURL=create-form.js.map